import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit, Plus, Trash2, MapPin } from 'lucide-react';
import { CustomerAddress } from '@/models/customer';
import {
    useAddCustomerAddress,
    useDeleteCustomerAddress,
    useUpdateCustomerAddress,
} from '@/api/customers/use-customers';
import { useConfirm } from '@/hooks/useConfirm';

interface CustomerAddressesWidgetProps {
    customerId: string;
    addresses: CustomerAddress[];
}

interface AddressFormData {
    type: 'billing' | 'shipping' | 'residential' | 'business';
    address_line_1: string;
    address_line_2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    is_default: boolean;
}

const initialAddressForm: AddressFormData = {
    type: 'residential',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    is_default: false,
};

const CustomerAddressesWidget: React.FC<CustomerAddressesWidgetProps> = ({
    customerId,
    addresses,
}) => {
    const confirm = useConfirm();
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<CustomerAddress | null>(null);
    const [formData, setFormData] = useState<AddressFormData>(initialAddressForm);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [deletingAddressId, setDeletingAddressId] = useState<string | null>(null);

    // Use the API hooks
    const addAddressMutation = useAddCustomerAddress(customerId);
    const updateAddressMutation = useUpdateCustomerAddress(customerId, selectedAddress?.id || '');
    const deleteAddressMutation = useDeleteCustomerAddress(customerId);

    const handleAddAddress = async () => {
        try {
            await addAddressMutation.mutateAsync(formData);
            handleCloseAddDialog();
        } catch (error) {
            console.error('Error adding address:', error);
        }
    };

    const handleUpdateAddress = async () => {
        if (!selectedAddress) return;

        try {
            await updateAddressMutation.mutateAsync(formData);
            handleCloseEditDialog();
        } catch (error) {
            console.error('Error updating address:', error);
        }
    };

    const handleDeleteAddress = async (addressId: string) => {
        const ok = await confirm({
            title: 'Delete address?',
            description:
                'Are you sure you want to delete this address? This action cannot be undone.',
            confirmLabel: 'Delete',
            variant: 'destructive',
        });
        if (!ok) return;

        try {
            setDeletingAddressId(addressId);
            await deleteAddressMutation.mutateAsync(addressId);
        } catch (error) {
            console.error('Error deleting address:', error);
        } finally {
            setDeletingAddressId(null);
        }
    };

    const handleOpenAddDialog = () => {
        setFormData(initialAddressForm);
        setFormErrors({});
        setIsAddDialogOpen(true);
    };

    const handleCloseAddDialog = () => {
        setIsAddDialogOpen(false);
    };

    const handleOpenEditDialog = (address: CustomerAddress) => {
        setSelectedAddress(address);
        // Handle potential field name mismatches
        const addressLine1 =
            address.address_line_1 ||
            (address as any).address_line1 ||
            (address as any).addressLine1 ||
            '';
        const addressLine2 =
            address.address_line_2 ||
            (address as any).address_line2 ||
            (address as any).addressLine2 ||
            '';

        setFormData({
            type: address.type,
            address_line_1: addressLine1,
            address_line_2: addressLine2,
            city: address.city,
            state: address.state,
            postal_code: address.postal_code,
            country: address.country,
            is_default: address.is_default,
        });
        setFormErrors({});
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setSelectedAddress(null);
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, checked } = e.target;
        if (name) {
            if (name === 'is_default') {
                setFormData(prev => ({ ...prev, [name]: checked }));
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
            }

            // Clear error when field is edited
            if (formErrors[name]) {
                setFormErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
    };

    const handleSelectChange = (name: string) => (value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.address_line_1) {
            errors.address_line_1 = 'Address line 1 is required';
        }

        if (!formData.city) {
            errors.city = 'City is required';
        }

        if (!formData.state) {
            errors.state = 'State is required';
        }

        if (!formData.postal_code) {
            errors.postal_code = 'Postal code is required';
        }

        if (!formData.country) {
            errors.country = 'Country is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (isEdit: boolean) => {
        if (!validateForm()) {
            return;
        }

        if (isEdit) {
            handleUpdateAddress();
        } else {
            handleAddAddress();
        }
    };

    const renderAddressDialog = (isEdit: boolean) => (
        <Dialog
            open={isEdit ? isEditDialogOpen : isAddDialogOpen}
            onOpenChange={isEdit ? setIsEditDialogOpen : setIsAddDialogOpen}
        >
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Address' : 'Add New Address'}</DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                        <Label htmlFor="type">Address Type</Label>
                        <Select value={formData.type} onValueChange={handleSelectChange('type')}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select address type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="billing">Billing</SelectItem>
                                <SelectItem value="shipping">Shipping</SelectItem>
                                <SelectItem value="residential">Residential</SelectItem>
                                <SelectItem value="business">Business</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex items-center space-x-2 pt-6">
                        <Checkbox
                            id="is_default"
                            checked={formData.is_default}
                            onCheckedChange={checked =>
                                setFormData(prev => ({ ...prev, is_default: checked as boolean }))
                            }
                        />
                        <Label htmlFor="is_default" className="text-sm">
                            Set as default address
                        </Label>
                    </div>

                    <div className="col-span-full space-y-2">
                        <Label htmlFor="address_line_1">Address Line 1 *</Label>
                        <Input
                            id="address_line_1"
                            name="address_line_1"
                            value={formData.address_line_1}
                            onChange={handleFormChange}
                            className={formErrors.address_line_1 ? 'border-destructive' : ''}
                            required
                        />
                        {formErrors.address_line_1 && (
                            <p className="text-sm text-destructive">{formErrors.address_line_1}</p>
                        )}
                    </div>

                    <div className="col-span-full space-y-2">
                        <Label htmlFor="address_line_2">Address Line 2</Label>
                        <Input
                            id="address_line_2"
                            name="address_line_2"
                            value={formData.address_line_2 || ''}
                            onChange={handleFormChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleFormChange}
                            className={formErrors.city ? 'border-destructive' : ''}
                            required
                        />
                        {formErrors.city && (
                            <p className="text-sm text-destructive">{formErrors.city}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="state">State/Province *</Label>
                        <Input
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleFormChange}
                            className={formErrors.state ? 'border-destructive' : ''}
                            required
                        />
                        {formErrors.state && (
                            <p className="text-sm text-destructive">{formErrors.state}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="postal_code">Postal Code *</Label>
                        <Input
                            id="postal_code"
                            name="postal_code"
                            value={formData.postal_code}
                            onChange={handleFormChange}
                            className={formErrors.postal_code ? 'border-destructive' : ''}
                            required
                        />
                        {formErrors.postal_code && (
                            <p className="text-sm text-destructive">{formErrors.postal_code}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="country">Country *</Label>
                        <Input
                            id="country"
                            name="country"
                            value={formData.country}
                            onChange={handleFormChange}
                            className={formErrors.country ? 'border-destructive' : ''}
                            required
                        />
                        {formErrors.country && (
                            <p className="text-sm text-destructive">{formErrors.country}</p>
                        )}
                    </div>
                </div>

                <DialogFooter className="mt-6">
                    <Button
                        variant="outline"
                        onClick={isEdit ? handleCloseEditDialog : handleCloseAddDialog}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleSubmit(isEdit)}
                        disabled={
                            isEdit ? updateAddressMutation.isPending : addAddressMutation.isPending
                        }
                    >
                        {isEdit
                            ? updateAddressMutation.isPending
                                ? 'Saving...'
                                : 'Save Changes'
                            : addAddressMutation.isPending
                              ? 'Adding...'
                              : 'Add Address'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Addresses ({addresses.length})
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {addresses.length === 0 ? (
                    <div className="text-center py-6">
                        <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">No addresses found</p>
                        <Button
                            variant="outline"
                            onClick={handleOpenAddDialog}
                            className="flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Add First Address
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {addresses.map(address => (
                            <div
                                key={address.id}
                                className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge variant="outline" className="capitalize">
                                            {address.type}
                                        </Badge>
                                        {address.is_default && (
                                            <Badge variant="default" className="text-xs">
                                                Default
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-sm">
                                        <p className="font-medium">
                                            {address.address_line_1 ||
                                                (address as any).address_line1 ||
                                                (address as any).addressLine1 ||
                                                ''}
                                        </p>
                                        {(address.address_line_2 ||
                                            (address as any).address_line2 ||
                                            (address as any).addressLine2) && (
                                            <p className="text-muted-foreground">
                                                {address.address_line_2 ||
                                                    (address as any).address_line2 ||
                                                    (address as any).addressLine2}
                                            </p>
                                        )}
                                        <p className="text-muted-foreground">
                                            {address.city}, {address.state} {address.postal_code}
                                        </p>
                                        <p className="text-muted-foreground">{address.country}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1 ml-4">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleOpenEditDialog(address)}
                                        className="h-8 w-8 p-0"
                                        aria-label="Edit address"
                                        title="Edit address"
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteAddress(address.id)}
                                        disabled={deletingAddressId === address.id}
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                        aria-label="Delete address"
                                        title="Delete address"
                                    >
                                        {deletingAddressId === address.id ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                        ) : (
                                            <Trash2 className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ))}
                        <div className="pt-2 border-t">
                            <Button
                                variant="outline"
                                onClick={handleOpenAddDialog}
                                className="w-full flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add Address
                            </Button>
                        </div>
                    </div>
                )}

                {renderAddressDialog(false)}
                {renderAddressDialog(true)}
            </CardContent>
        </Card>
    );
};

export default CustomerAddressesWidget;
